<template>
  <Teleport to="body">
    <div v-if="isVisible" class="picker-backdrop" @click.self="close">
      <div class="picker-sheet" role="dialog" aria-modal="true" aria-labelledby="hero-picker-title">
        <header class="picker-header">
          <h2 id="hero-picker-title">Assign Hero</h2>
          <button
            type="button"
            class="picker-close"
            aria-label="Close"
            title="Close"
            @click="close"
          >
            <i class="fas fa-times"></i>
          </button>
        </header>
        <div class="picker-list">
          <button
            v-for="hero in gameStore.playerHeroes"
            :key="hero.id"
            type="button"
            class="picker-hero"
            :class="{
              busy: isHeroInProgress(hero.id),
              queued: plannedQuestTitle(hero.id) && !isHeroInProgress(hero.id),
              selected: selectedHeroId === hero.id,
            }"
            :disabled="isHeroInProgress(hero.id)"
            @click="selectHero(hero)"
          >
            <HeroTagChip
              block
              :name="heroDisplayName(hero)"
              :meta="`Hero · Level ${hero.level || 1}`"
              :image="socketedCard(hero)?.image || ''"
              :image-position="socketedCard(hero)?.image_position"
              :rarity="socketedCard(hero)?.rarity || 'common'"
            >
              <span v-if="isHeroInProgress(hero.id)" class="unit-status">On a mission</span>
              <span v-else-if="plannedQuestTitle(hero.id)" class="unit-status">
                Queued: {{ plannedQuestTitle(hero.id) }}
              </span>
            </HeroTagChip>
          </button>
          <p v-if="!gameStore.playerHeroes.length" class="empty-msg">
            Activate a hero to assign them.
          </p>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, watch, onUnmounted } from "vue";
import { useGameStore } from "../store/game";
import HeroTagChip from "./HeroTagChip.vue";

const props = defineProps({
  isVisible: {
    type: Boolean,
    default: false,
  },
  questSlug: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["close"]);

const gameStore = useGameStore();

const selectedHeroId = computed(() => {
  if (!props.questSlug) return null;
  return gameStore.assignmentForQuest(props.questSlug).assigned_hero_id || null;
});

const heroDisplayName = (hero) => {
  if (!hero) return "Hero";
  return hero.name || `Hero #${hero.id}`;
};

const socketedCard = (hero) => gameStore.socketedHeroCard(hero?.id);

const isHeroInProgress = (heroId) => gameStore.inProgressHeroIds.has(heroId);

const plannedQuestTitle = (heroId) => {
  const roster = gameStore.plannedRosters.find((r) => r.hero?.id === heroId);
  return roster?.title || null;
};

const close = () => emit("close");

const selectHero = (hero) => {
  if (!hero?.id || isHeroInProgress(hero.id) || !props.questSlug) return;
  gameStore.assignUnitToQuest(props.questSlug, { type: "hero", id: hero.id });
  close();
};

function onKeydown(event) {
  if (event.key === "Escape" && props.isVisible) {
    close();
  }
}

watch(
  () => props.isVisible,
  (visible) => {
    if (visible) {
      window.addEventListener("keydown", onKeydown);
    } else {
      window.removeEventListener("keydown", onKeydown);
    }
  }
);

onUnmounted(() => {
  window.removeEventListener("keydown", onKeydown);
});
</script>

<style scoped>
.picker-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);
  z-index: 1300;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.picker-sheet {
  width: 100%;
  max-width: 480px;
  max-height: min(78dvh, 560px);
  background: rgba(10, 15, 30, 0.98);
  border: 1px solid #00f6ff;
  border-bottom: none;
  border-radius: 16px 16px 0 0;
  box-shadow: 0 -8px 28px rgba(0, 0, 0, 0.55);
  display: flex;
  flex-direction: column;
  padding-bottom: env(safe-area-inset-bottom, 0px);
}

.picker-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.9rem 1rem;
  border-bottom: 1px solid rgba(0, 246, 255, 0.2);
}

.picker-header h2 {
  margin: 0;
  font-size: 1rem;
  color: #00f6ff;
  font-family: "Orbitron", sans-serif;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.picker-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background: none;
  border: 1px solid rgba(0, 246, 255, 0.3);
  color: #a0a0b0;
  border-radius: 6px;
  cursor: pointer;
}

.picker-list {
  overflow-y: auto;
  padding: 0.9rem 1rem 1.1rem;
  -webkit-overflow-scrolling: touch;
}

.picker-hero {
  display: block;
  width: 100%;
  margin-bottom: 12px;
  padding: 0;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
}

.picker-hero.busy {
  cursor: not-allowed;
  opacity: 0.55;
}

.picker-hero.queued :deep(.hero-tag-chip),
.picker-hero.selected :deep(.hero-tag-chip) {
  box-shadow: 0 0 12px rgba(0, 246, 255, 0.35);
}

.unit-status {
  font-size: 0.75rem;
  color: #ebcb8b;
}

.empty-msg {
  text-align: center;
  color: #888;
  font-style: italic;
  margin: 1.5rem 0;
}
</style>
